import { lazy, Suspense, useContext } from "react";
import { Provider } from "react-redux";
import { Router, Route, Switch, Redirect } from "react-router-dom";
import AppContainer from "./container/appContainer";
import LoginContainer from "./container/loginContainer";
import MockContainer from "./container/mockContainer";
import { store } from "./data/store/configStore";
import GlobalFallback from "./components/fallback/globalFallback";
import { CommonPortalControllerHOC } from './controllers/commonPortal';
import { CommonPortalContext } from './contexts/commonPortal';
import { History as history } from "./utils/customUtils";
import { FEATURE } from './utils/feature';
import { VISITS_URL_V2 } from './constants/patientChart.constant';
import "./index.css";

const LoginComponent = lazy(() => import('./components/login'));
const Handshake = lazy(() => import('./container/handshakeContainer'));
const BuildStatus = lazy(() => import('./components/buildStatus'));
const Uploads = lazy(() => import('./container/uploadViewContainer'));
const Pdfs = lazy(() => import('./container/uploadPDFContainer'));
const PatientChart = lazy(() => import('./pages/patientChart'));
const PatientChartV3 = lazy(() => import('./pages/patientChart/v2'));
const PatientHistory = lazy(() => import('./pages/patientHistory'));
const PageNotFound = lazy(() => import('./pages/pageNotFound'));
const ScheduledVisits = lazy(() => import('./pages/scheduledVisits'));
const AsyncOrders = lazy(() => import('./pages/asyncOrders'));
const SearchOrders = lazy(() => import('./pages/searchOrders'));
const Tasks = lazy(() => import('./pages/tasks'));
const RecoverPassword = lazy(() => import('./components/recoverPassword'));
const ChangePassword = lazy(() => import('./pages/changePassword'));
const ChangePIN = lazy(() => import('./pages/changePIN'));
const Profile = lazy(() => import('./pages/profile'));
const Availability = lazy(() => import('./pages/availability'));
const Reports = lazy(() => import('./pages/reports'));
const DosespotJumpstart = lazy(() => import('./pages/dosespotJumpstart'));
const Snackbar = lazy(() => import('./components/commonPortal/Snackbar'));
const FSPopup = lazy(() => import('./components/commonPortal/FSPopup'));
const LegacyPatientChart = lazy(() => import('./components/app'));
const LegacyHistory = lazy(() => import('./container/historyViewContainer'));

const hasFeature = FEATURE();

const pages = [
  {
    key: 'login',
    exact: true,
    path: '/',
    hoc: LoginContainer,
    Component: LoginComponent,
  },
  {
    key: 'change-password',
    exact: false,
    path: '/change-password',
    hoc: AppContainer,
    Component: ChangePassword,
  },
  {
    key: 'change-pin',
    exact: false,
    path: '/change-pin',
    hoc: AppContainer,
    Component: ChangePIN,
  },
  {
    key: 'availability',
    exact: false,
    path: '/availability',
    hoc: AppContainer,
    Component: Availability,
  },
  {
    key: 'recover-password',
    exact: false,
    path: '/recover-password',
    hoc: AppContainer,
    Component: RecoverPassword,
  },
  {
    key: 'handshake',
    exact: true,
    path: '/handshake',
    hoc: MockContainer,
    Component: Handshake,
  },
  {
    key: 'patientchart-v1',
    exact: true,
    path: '/visits',
    hoc: AppContainer,
    Component: LegacyPatientChart,
  },  
  {
    key: 'patientchart-v2',
    exact: false,
    path: '/patientchart/:orderId/:patientId',
    hoc: AppContainer,
    Component: PatientChart,
  },
  {
    key: 'scheduled-visits',
    exact: false,
    path: VISITS_URL_V2,
    hoc: AppContainer,
    Component: ScheduledVisits,
  },
  {
    key: 'async-orders',
    exact: false,
    path: '/async-orders',
    hoc: AppContainer,
    Component: AsyncOrders,
  },
  {
    key: 'search-orders',
    exact: false,
    path: '/search-orders',
    hoc: AppContainer,
    Component: SearchOrders,
  },
  {
    key: 'orderhistory',
    exact: false,
    path: '/orderhistory/:orderId/:patientId',
    hoc: AppContainer,
    Component: PatientHistory,
  },
  {
    key: 'tasks',
    exact: false,
    path: '/tasks',
    hoc: AppContainer,
    Component: Tasks,
  },
  {
    key: 'completed-visits-report',
    exact: false,
    path: '/completed-visits-report',
    hoc: AppContainer,
    Component: Reports,
  },
  {
    key: 'profile',
    exact: false,
    path: '/profile',
    hoc: AppContainer,
    Component: Profile,
  },
  {
    key: 'dosespot-jumpstart',
    exact: false,
    path: '/dosespot-jumpstart',
    hoc: AppContainer,
    Component: DosespotJumpstart,
  },
  {
    key: 'pnf',
    exact: false,
    path: '/pnf',
    hoc: AppContainer,
    Component: PageNotFound,
  },
  {
    key: 'build-status',
    exact: true,
    path: '/build-status',
    hoc: MockContainer,
    Component: BuildStatus,
  },
  {
    key: 'legacy-history',
    exact: true,
    path: '/history/:orderId/:userId',
    hoc: MockContainer,
    Component: LegacyHistory,
  },
  {
    key: 'uploads',
    exact: true,
    path: '/viewuploads/:orderId/:patientId/:questionId',
    hoc: MockContainer,
    Component: Uploads,
  },
  {
    key: 'pdfs',
    exact: true,
    path: '/viewpdf/:orderId',
    hoc: MockContainer,
    Component: Pdfs,
  },
];

export const Routes = () => {
  const { ref: { snackbar, fsPopup } } = useContext(CommonPortalContext);

  return (
    <Suspense fallback={<GlobalFallback />}>
      <Provider store={store}>
        <Router history={history}>
          <Switch>
            {
              pages.map(({ key, exact, path, hoc, Component }) => (
                <Route
                  key={key}
                  exact={exact}
                  path={path}
                  component={hoc(Component)}
                />
              ))
            }
            {
              (hasFeature.PATIENT_CHART_V3) && (
                <Route
                  path='/patientchart-v3/:orderId/:patientId'
                  component={AppContainer(PatientChartV3)}
                />
              )
            }
            <Route
              path='*'
              render={() => {
                return (
                  <Redirect to={VISITS_URL_V2} />
                )
              }}
            />
    
          </Switch>
        </Router>
    
        <Snackbar ref={snackbar} />
        <FSPopup ref={fsPopup} />
      </Provider>
    </Suspense>
  );
}

export default CommonPortalControllerHOC(Routes);
